import { useEffect, useState } from 'react';
import { fetchContractsByIds } from '../../../services/contractLookup';
import { dedupeContracts, normalizeContract } from '../../../services/possessionMatcher';

const UNKNOWN = /^UNKNOWN\b/i;
const cache = new Map(); // contractNo -> customer name (session-wide)

/** Customer names straight from the yelo database for the given contract numbers. */
export async function resolveCustomerNames(contractNos) {
    const wanted = [...new Set((contractNos || []).map((c) => String(c || '').trim().toUpperCase()).filter(Boolean))]
        .filter((c) => !cache.has(c));
    if (wanted.length) {
        try {
            const docs = await fetchContractsByIds(wanted);
            const records = dedupeContracts(docs.map((d) => normalizeContract(d)));
            for (const r of records) if (r.customer) cache.set(r.contractNo, r.customer);
            for (const c of wanted) if (!cache.has(c)) cache.set(c, '');
        } catch (e) { /* offline or no permission: keep the stored names */ }
    }
    const out = {};
    for (const c of (contractNos || [])) { const k = String(c || '').trim().toUpperCase(); if (cache.get(k)) out[k] = cache.get(k); }
    return out;
}

/**
 * The fines engine stores the customer name it matched at processing time. The
 * contract itself lives in the yelo database, so the page reads the name from
 * there whenever the stored one is missing/unknown - no processing needed.
 */
export function useLiveCustomerNames(rows) {
    const [names, setNames] = useState({});
    const key = (rows || []).filter((r) => r?.contract_no && (!r.customer || UNKNOWN.test(r.customer)))
        .map((r) => r.contract_no).join(',');
    useEffect(() => {
        if (!key) return undefined;
        let alive = true;
        resolveCustomerNames(key.split(',')).then((n) => { if (alive) setNames(n); });
        return () => { alive = false; };
    }, [key]);
    return names;
}

/** Display name for a fine: stored name, else the live one from the database. */
export const customerOf = (fine, liveNames) => {
    if (fine?.customer && !UNKNOWN.test(fine.customer)) return fine.customer;
    const live = liveNames?.[String(fine?.contract_no || '').trim().toUpperCase()];
    return live || fine?.customer || '';
};
